/**
 * Utility functions for handling authentication errors and cleanup
 */

// refresh token error patterns
const REFRESH_TOKEN_PATTERNS =
  /InvalidRefreshTokenException|Invalid Refresh Token|Refresh Token has expired|Refresh Token has been revoked|NotAuthorizedException|400/i;

// user-specific fatal error patterns
const USER_FATAL_PATTERNS =
  /UserNotFoundException|UserNotFound|UserDisabledException|user.*disabled/i;

/**
 * Checks if current path is in MFA or registration flow
 * @param pathname - Current route pathname
 * @returns True if in MFA or registration flow
 */
export const isInMFAFlow = (pathname: string): boolean => {
  return (
    pathname.startsWith('/registration/mfa') ||
    pathname.includes('mfa') ||
    pathname.startsWith('/registration')
  );
};

/**
 * Detects if error is a refresh token error
 * @param error - The error to check
 * @param pathname - Current route pathname
 * @returns True if error is related to refresh token failure
 */
export const isRefreshTokenError = (
  error: unknown,
  pathname: string,
): boolean => {
  // Don't treat as refresh token error during MFA flow
  if (isInMFAFlow(pathname)) {
    return false;
  }

  const errorMessage = (error as Error)?.message || '';
  const errorString = String(error);

  return (
    REFRESH_TOKEN_PATTERNS.test(errorMessage) ||
    REFRESH_TOKEN_PATTERNS.test(errorString)
  );
};

/**
 * Clears SSO-related data from localStorage and cookies
 * Use this when SSO authentication fails or during logout
 */
export const clearSSOStorage = (): void => {
  localStorage.removeItem('isSSOLogin');
  localStorage.removeItem('sso_domain');
  localStorage.removeItem('docs_id_token');
  document.cookie =
    'sso_domain=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

/**
 * Clears all Cognito-related authentication data from storage
 * Performs aggressive cleanup of localStorage, sessionStorage, and cookies
 */
export const clearCognitoStorage = (): void => {
  // Clear localStorage - remove all Cognito and auth-related keys
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    if (
      key.includes('CognitoIdentityServiceProvider') ||
      key.includes('amplify') ||
      key.includes('aws.cognito') ||
      key.includes('docs_id_token') ||
      key.includes('session_id_token') ||
      key.includes('isEulaAccepted')
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Clear sessionStorage
  sessionStorage.clear();

  // Clear SSO cookies
  document.cookie =
    'sso_domain=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

/**
 * Determines if navigation to login should occur based on current path
 * @param pathname - Current route pathname
 * @returns True if should redirect to login
 */
export const shouldNavigateToLogin = (pathname: string): boolean => {
  return (
    !isInMFAFlow(pathname) &&
    !pathname.startsWith('/login') &&
    !pathname.startsWith('/registration') &&
    !pathname.startsWith('/privacy-policy') &&
    !window.location.search.includes('code=')
  );
};

/**
 * Checks if auth check should be skipped on public/auth pages
 * @param pathname - Current route pathname
 * @param hasUser - Whether a user is currently authenticated
 * @returns True if auth check should be skipped
 */
export const shouldSkipAuthCheck = (
  pathname: string,
  hasUser: boolean,
): boolean => {
  // MFA routes always need auth check (user must be authenticated)
  if (pathname.includes('mfa')) {
    return false;
  }

  // Privacy policy with authenticated user - skip check
  if (pathname === '/privacy-policy' && hasUser) {
    return true;
  }

  // Login/registration pages without user - skip check
  if (
    (pathname.startsWith('/login') || pathname.startsWith('/registration')) &&
    !hasUser
  ) {
    return true;
  }

  return false;
};

/**
 * Checks if an error is a fatal authentication error that requires sign out
 * @param error - The error to check
 * @returns True if error is fatal and requires sign out
 */
export const isFatalAuthError = (error: unknown): boolean => {
  const msg = (error as Error)?.message || '';
  const errorString = String(error);

  return (
    USER_FATAL_PATTERNS.test(msg) ||
    REFRESH_TOKEN_PATTERNS.test(msg) ||
    REFRESH_TOKEN_PATTERNS.test(errorString)
  );
};
